import { GCEnhancementType } from '@sharethrough/groundcontrol-enhancement-flush';
import { isSupportedLanguage } from '@web-rendering/groundcontrol-butler-helpers';
import type { Creative, Network, Payload } from '@web-rendering/groundcontrol-core';
import { CreativeType } from '@web-rendering/groundcontrol-shared-types';
import {
  isValidCreativeType,
  parseEnhancementPayload,
  PayloadService,
  SOFIA_PULSE_ENHANCEMENT_TYPES
} from '@web-rendering/groundcontrol-core';
import type { NativeType } from '@web-rendering/groundcontrol-core';
import { reportError } from '@web-rendering/groundcontrol-monitoring';
import { OrtbEventTrackers, setNativeAssets } from '@web-rendering/groundcontrol-ortb';
import { setRawPayload } from '@web-rendering/groundcontrol-store';
import {
  type Enhancement,
  type EnhancementType,
  RawEnhancementType,
  type RawPayload
} from '@web-rendering/groundcontrol-shared-types';
import { substituteTemplateMacros } from './substitute-template-macros';

const getEnhancementType = (rawEnhancementType: RawEnhancementType): EnhancementType => {
  switch (rawEnhancementType) {
    case RawEnhancementType.Disabled:
      return 'disabled';
    case RawEnhancementType.Default:
      return 'default';
    case RawEnhancementType.OutstreamCaptions:
      return GCEnhancementType.OutstreamCaptions;
    case RawEnhancementType.OutstreamCountdown:
      return GCEnhancementType.OutstreamCountdown;
    case RawEnhancementType.OutstreamHeadline:
      return GCEnhancementType.OutstreamHeadline;
    case RawEnhancementType.OutstreamDailyWeatherOutlook:
      return GCEnhancementType.OutstreamDailyWeatherOutlook;
    case RawEnhancementType.OutstreamSportsTicker:
      return GCEnhancementType.OutstreamSportsTicker;
    case RawEnhancementType.OutstreamWeather:
      return GCEnhancementType.OutstreamWeather;
    case RawEnhancementType.BannerCountdown:
      return GCEnhancementType.BannerCountdown;
    case RawEnhancementType.BannerHeadline:
      return GCEnhancementType.BannerHeadline;
    case RawEnhancementType.BannerDailyWeatherOutlook:
      return GCEnhancementType.BannerDailyWeatherOutlook;
    case RawEnhancementType.BannerWeather:
      return GCEnhancementType.BannerWeather;
    case RawEnhancementType.BannerSportsTicker:
      return GCEnhancementType.BannerSportsTicker;
    case RawEnhancementType.BannerShoppable:
      return GCEnhancementType.BannerShoppable;
    case RawEnhancementType.OutstreamShoppable:
      return GCEnhancementType.OutstreamShoppable;
    case RawEnhancementType.BannerMedalCount:
      return GCEnhancementType.BannerMedalCount;
    case RawEnhancementType.OutstreamMedalCount:
      return GCEnhancementType.OutstreamMedalCount;
    case RawEnhancementType.BannerBrandedHeadline:
      return GCEnhancementType.BannerBrandedHeadline;
    case RawEnhancementType.OutstreamBrandedHeadlines:
      return GCEnhancementType.OutstreamBrandedHeadline;
    case RawEnhancementType.BannerShoppableOverlay:
      return GCEnhancementType.BannerShoppableOverlay;
    case RawEnhancementType.BannerWorldTournament:
      return GCEnhancementType.BannerWorldTournament;
    case RawEnhancementType.OutstreamWorldTournament:
      return GCEnhancementType.OutstreamWorldTournament;
    case RawEnhancementType.BannerCustom:
      return GCEnhancementType.BannerCustom;
    case RawEnhancementType.OutstreamCustom:
      return GCEnhancementType.OutstreamCustom;
    case RawEnhancementType.BannerSofiaPulseSportsHeadlines:
      return GCEnhancementType.BannerSofiaPulseSportsHeadlines;
    case RawEnhancementType.BannerSofiaPulseQuiz:
      return GCEnhancementType.BannerSofiaPulseQuiz;
    case RawEnhancementType.BannerSofiaPulseBettingOdds:
      return GCEnhancementType.BannerSofiaPulseBettingOdds;
    case RawEnhancementType.BannerSofiaPulseStockTicker:
      return GCEnhancementType.BannerSofiaPulseStockTicker;
    default:
      return 'default';
  }
};

export class EquativPayloadService extends PayloadService {
  parse(rawPayload: RawPayload): Payload {
    setRawPayload(rawPayload);

    try {
      return {
        id: rawPayload.id,
        creative: this.getCreative(rawPayload),
        network: this.getNetwork(rawPayload),
        reporting: this.getReporting(rawPayload),
        enhancement: this.getEnhancement(rawPayload),
        scenario: rawPayload.scenario
      };
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      throw new Error('Error parsing payload: ' + errorMsg);
    }
  }

  getCreative(rawPayload: RawPayload): Creative {
    if (!isValidCreativeType(rawPayload.creative.type)) {
      throw new Error('Invalid creative type');
    }

    if (rawPayload.creative.type === CreativeType.Native) {
      const parsedContent = JSON.parse(rawPayload.creative.content);

      if (!Array.isArray(parsedContent)) {
        throw new Error('Content must be an array for native creatives');
      }

      setNativeAssets(parsedContent);

      return {
        type: CreativeType.Native,
        content: parsedContent,
        language: isSupportedLanguage(rawPayload.creative.language) ? rawPayload.creative.language : 'en',
        size: rawPayload.creative.size,
        behaviors: rawPayload.creative.behaviors ?? [],
        partnerId: rawPayload.creative.partnerId.toString(),
        seatId: rawPayload.creative.seatId,
        creativeId: rawPayload.creative.creativeId,
        dealId: rawPayload.creative.dealId,
        trackers: new OrtbEventTrackers(rawPayload.creative.eventtrackers ?? []),
        link: rawPayload.creative.link ?? '',
        // TODO: Remove this once we stop supporting NAG creatives
        nativeType: (rawPayload.creative.nativeType ?? 'clickout') as `${NativeType}`,
        assetsurl: rawPayload.creative.assetsurl
      };
    }

    if (Array.isArray(rawPayload.creative.content)) {
      throw new Error('Content must be a string for banner and outstream creatives');
    }

    return {
      type: rawPayload.creative.type,
      content: rawPayload.creative.content,
      language: isSupportedLanguage(rawPayload.creative.language) ? rawPayload.creative.language : 'en',
      size: rawPayload.creative.size,
      behaviors: rawPayload.creative.behaviors ?? [],
      partnerId: rawPayload.creative.partnerId.toString(),
      seatId: rawPayload.creative.seatId,
      creativeId: rawPayload.creative.creativeId,
      dealId: rawPayload.creative.dealId,
      link: rawPayload.creative.link ?? ''
    };
  }

  getNetwork(rawPayload: RawPayload): Network {
    return {
      networkId: rawPayload.network.networkId.toString(),
      size: {
        width: rawPayload.network.size.width,
        height: rawPayload.network.size.height
      },
      bundleId: rawPayload.network.bundleId ?? undefined,
      domain: rawPayload.network.domain,
      callerId: rawPayload.network.callerId.toString(),
      country: rawPayload.network.country
    };
  }

  getEnhancement(rawPayload: RawPayload): Enhancement {
    const enhancementType = getEnhancementType(rawPayload.enhancement.type);
    const enhancementPayload = parseEnhancementPayload(rawPayload.enhancement.payload);
    if (enhancementPayload) {
      if (SOFIA_PULSE_ENHANCEMENT_TYPES.has(enhancementType) && 'templateUrl' in enhancementPayload) {
        return {
          type: enhancementType,
          payload: {
            ...enhancementPayload,
            templateUrl: substituteTemplateMacros(enhancementPayload.templateUrl, rawPayload)
          },
          enhancement_version_id: rawPayload.enhancement.evid
        };
      }
      return {
        type: enhancementType,
        payload: enhancementPayload,
        enhancement_version_id: rawPayload.enhancement.evid
      };
    }

    if (enhancementType !== 'default' && enhancementType !== 'disabled') {
      reportError('Enhancement payload mismatch', {
        callerId: rawPayload.network.callerId.toString(),
        networkId: rawPayload.network.networkId.toString(),
        partnerId: rawPayload.creative.partnerId.toString(),
        enhancement: {
          type: rawPayload.enhancement.type,
          payload: rawPayload.enhancement.payload
        }
      });

      return {
        type: 'disabled'
      };
    }

    return {
      type: enhancementType
    };
  }

  getReporting(rawPayload: RawPayload) {
    return {
      trackerBaseUrl: rawPayload?.reporting?.trackerBaseUrl,
      trackerImpressionUrl: rawPayload?.reporting?.trackerImpressionUrl,
      countImpressionOnePixel: rawPayload?.reporting?.countImpressionOnePixel ?? false,
      disableClickCounting: rawPayload?.reporting?.disableClickCounting
    };
  }
}
